import { type ReactNode } from 'react';

export interface TableColumn<T> {
  key?: string;
  accessor?: string;  // Alias for key (for compatibility)
  header: string;
  render?: (item: T) => ReactNode;
  className?: string;
  hideOnMobile?: boolean;
  isPrimary?: boolean;
}

export interface TableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  keyExtractor: (item: T) => string | number;
  className?: string;
  emptyMessage?: string;
  onRowClick?: (item: T) => void;
  stickyHeader?: boolean;
}

export function Table<T>({
  data,
  columns,
  keyExtractor,
  className = '',
  emptyMessage = 'No data available',
  onRowClick,
  stickyHeader = false,
}: TableProps<T>) {
  if (data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  // Find primary column for sticky behavior
  const primaryColumn = columns.find((col) => col.isPrimary);

  return (
    <div className={`table-container overflow-x-auto -webkit-overflow-scrolling-touch ${className}`}>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className={`bg-gray-50 ${stickyHeader ? 'sticky top-0 z-10' : ''}`}>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key || column.accessor || column.header}
                className={`
                  px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider
                  ${column.hideOnMobile ? 'hidden sm:table-cell' : ''}
                  ${column.isPrimary ? 'sticky left-0 bg-gray-50 z-20 shadow-[2px_0_4px_rgba(0,0,0,0.05)]' : ''}
                  ${column.className || ''}
                `}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((item) => (
            <tr
              key={keyExtractor(item)}
              className={`
                hover:bg-gray-50 transition-colors
                ${onRowClick ? 'cursor-pointer active:bg-gray-100' : ''}
              `}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
            >
              {columns.map((column) => (
                <td
                  key={column.key || column.accessor || column.header}
                  className={`
                    px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm
                    ${column.hideOnMobile ? 'hidden sm:table-cell' : ''}
                    ${column.isPrimary ? 'sticky left-0 bg-white z-10 shadow-[2px_0_4px_rgba(0,0,0,0.05)]' : ''}
                    ${column.className || ''}
                  `}
                >
                  {column.render
                    ? column.render(item)
                    : String((item as Record<string, unknown>)[column.key || column.accessor || ''] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
